import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './ChatWidget.css';
import { useChatContext } from '../contexts/ChatContext';

const ChatWidget = () => {
  const { pageContext } = useChatContext();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);

  const abortRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const stopStreaming = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setStreaming(false);
  }, []);

  const sendMessage = async () => {
    const trimmed = input.trim();
    if (!trimmed || streaming) return;
    setError(null);

    const userMsg = { role: 'user', content: trimmed };
    const history = [...messages, userMsg];
    setMessages(history);
    setInput('');
    setStreaming(true);

    const apiMessages = history.map(m => ({ role: m.role, content: m.content }));

    const assistantIdx = history.length;
    setMessages(prev => [...prev, { role: 'assistant', content: '', reasoning: '', toolEvents: [] }]);

    const appendToAssistant = (patch) => {
      setMessages(prev => prev.map((m, i) => i === assistantIdx ? { ...m, ...patch(m) } : m));
    };

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      console.log('[chat] POST /api/chat', { messageCount: apiMessages.length, hasContext: !!pageContext });
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
        body: JSON.stringify({ messages: apiMessages, context: pageContext || null }),
        signal: controller.signal,
      });

      const contentType = res.headers.get('content-type') || '';
      console.log('[chat] response', { status: res.status, contentType });

      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status}${body ? ` — ${body.slice(0, 300)}` : ''}`);
      }
      if (!res.body) {
        throw new Error('no response body');
      }
      if (!contentType.includes('text/event-stream')) {
        const body = await res.text().catch(() => '');
        throw new Error(
          `expected SSE but got ${contentType || 'no content-type'} — ` +
          `either /api/chat is not routed to the chat handler on the server, or the deployed build is stale. ` +
          `Response preview: ${body.slice(0, 200)}`
        );
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let idx;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
          const raw = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          if (!raw.trim()) continue;

          let event = 'message';
          let dataStr = '';
          for (const line of raw.split('\n')) {
            if (line.startsWith('event:')) event = line.slice(6).trim();
            else if (line.startsWith('data:')) dataStr += line.slice(5).trim();
          }
          let data = {};
          try { data = dataStr ? JSON.parse(dataStr) : {}; } catch { data = {}; }

          if (event === 'status') {
            console.log('[chat] status', data);
          } else if (event === 'token') {
            appendToAssistant(m => ({ content: (m.content || '') + (data.content || '') }));
          } else if (event === 'reasoning') {
            appendToAssistant(m => ({ reasoning: (m.reasoning || '') + (data.content || '') }));
          } else if (event === 'tool_call') {
            appendToAssistant(m => ({
              toolEvents: [...(m.toolEvents || []), { id: data.id, name: data.name, args: data.args, status: 'running' }],
            }));
          } else if (event === 'tool_result') {
            appendToAssistant(m => ({
              toolEvents: (m.toolEvents || []).map(t => t.id === data.id ? { ...t, status: data.ok ? 'ok' : 'error', error: data.error, chars: data.chars } : t),
            }));
          } else if (event === 'error') {
            const parts = [data.message || 'chat failed'];
            if (data.status) parts.push(`HTTP ${data.status}`);
            if (data.detail) parts.push(data.detail);
            setError(parts.join(' — '));
          } else if (event === 'done') {
            // fall through
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message || 'chat failed');
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    stopStreaming();
    setMessages([]);
    setError(null);
  };

  return (
    <>
      {!open && (
        <button className="chat-fab" onClick={() => setOpen(true)} aria-label="Open admin assistant">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </button>
      )}

      {open && (
        <div className="chat-panel" role="dialog" aria-label="Admin assistant">
          <div className="chat-header">
            <div className="chat-title">
              <span className="chat-dot" />
              <span>Admin Assistant</span>
            </div>
            <div className="chat-header-actions">
              <button className="chat-icon-btn" onClick={clearChat} title="Clear conversation" disabled={streaming}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6" />
                </svg>
              </button>
              <button className="chat-icon-btn" onClick={() => setOpen(false)} title="Close">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>

          {pageContext && (
            <div className="chat-context-pill" title={JSON.stringify(pageContext, null, 2)}>
              <span className="chat-context-icon">◉</span>
              <span className="chat-context-text">
                {pageContext.page === 'user_detail'
                  ? <>Viewing user <strong>{pageContext.user_email || pageContext.user_id}</strong></>
                  : <>Page context attached</>}
              </span>
            </div>
          )}

          <div className="chat-body">
            {messages.length === 0 && (
              <div className="chat-empty">
                <div className="chat-empty-title">Ask about users, invoices, contacts, subscriptions.</div>
                <div className="chat-empty-examples">
                  <button onClick={() => setInput('How many active users do we have?')}>How many active users do we have?</button>
                  <button onClick={() => setInput('Find the Stripe invoices for alex@qoddi.com')}>Find the Stripe invoices for alex@qoddi.com</button>
                  <button onClick={() => setInput('Show the most recent 10 users with daily_plan >= 2')}>Show the most recent 10 users with daily_plan &gt;= 2</button>
                </div>
              </div>
            )}

            {messages.map((m, i) => {
              const isStreaming = streaming && i === messages.length - 1;
              const isThinking = isStreaming && !m.content && !(m.toolEvents && m.toolEvents.length);
              return (
                <div key={i} className={`chat-msg chat-msg-${m.role}`}>
                  {m.reasoning && (
                    <details className="chat-reasoning" open={isThinking}>
                      <summary>
                        <span className="chat-reasoning-icon">{isThinking ? '◐' : '✦'}</span>
                        <span>{isThinking ? 'Thinking…' : 'Thought process'}</span>
                        <span className="chat-reasoning-count">{m.reasoning.length.toLocaleString()} chars</span>
                      </summary>
                      <div className="chat-reasoning-body">{m.reasoning}</div>
                    </details>
                  )}
                  {m.toolEvents && m.toolEvents.length > 0 && (
                    <div className="chat-tools">
                      {m.toolEvents.map((t, ti) => (
                        <div key={ti} className={`chat-tool chat-tool-${t.status}`}>
                          <span className="chat-tool-name">{t.name}</span>
                          <span className="chat-tool-status">
                            {t.status === 'running' && '…'}
                            {t.status === 'ok' && `✓${t.chars ? ` ${t.chars}ch` : ''}`}
                            {t.status === 'error' && `✗ ${t.error || 'error'}`}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  {m.content
                    ? (
                      <div className="chat-bubble">
                        {m.role === 'assistant'
                          ? <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                              a: (props) => <a {...props} target="_blank" rel="noopener noreferrer" />,
                            }}>{m.content}</ReactMarkdown>
                          : m.content}
                      </div>
                    )
                    : (isStreaming && !m.reasoning && <div className="chat-bubble chat-bubble-loading">…</div>)}
                </div>
              );
            })}

            {error && <div className="chat-error">Error: {error}</div>}

            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input-row">
            <textarea
              ref={inputRef}
              className="chat-input"
              placeholder="Ask about a user, invoice, contact…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={streaming}
            />
            {streaming ? (
              <button className="chat-send chat-stop" onClick={stopStreaming} title="Stop">Stop</button>
            ) : (
              <button className="chat-send" onClick={sendMessage} disabled={!input.trim()} title="Send">Send</button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
